/**
 * Vista Compartir — el corazón del panel del Promotor Digital.
 *
 * 3 botones, mismo link de afiliación (bienvenida.html?pv=<su_nodo>):
 *   - WhatsApp (wa.me)
 *   - Facebook / Instagram (sharer de Meta)
 *   - Copiar al portapapeles
 *
 * El mensaje es editable y se persiste en localStorage (default por idioma).
 * Si el nodo no tiene pasarela activa, muestra un banner de aviso.
 */

import PromoterConfig from '../config.js';
import { t } from '../languages.js';
import { montarLottie } from '../systems/lottie-icon.js';
import { rotarFrases } from './frases-hero.js';
import { codificarAfiliacion } from '@shared/services/codigo-afiliacion.js';
import { montarEditorMensaje } from './editor-mensaje.js';
import { renderFormato as formatoAHtml, aTextoPlano } from './preview-multicanal.js';
import { escapeHtml } from '../systems/escape-html.js';

export function renderCompartir({ config, user }) {
    const el = document.createElement('div');

    // Link de afiliación. urlJuego viene de GET /api/cashier/config (herencia
    // fractal del nodo). Fallback al .env si no vino.
    //
    // pv DEBE ser el NODO DE LA CAJA/PV del promotor (es el origen de atribución de
    // afiliados, cliente_nodos.es_origen, y a quién va la comisión). Es config.caja.
    // pvNodoId (el backend lo expone como el nodoId del cajaContext).
    //
    // CRÍTICO — NO usar user.nodo_id: un OPERADOR (ej. kaua@, nodo 3) puede operar su
    // caja Promotor Digital que vive en OTRO subárbol (nodo 78). user.nodo_id devuelve
    // el nodo del usuario (3), no el de la caja (78) → el link codificaba el nodo
    // equivocado → los jugadores se afiliaban al operador (sin pasarela) y Kaua nunca
    // cobraba. Bug real en prod (link a3g = nodo 3, debía ser 7dcn = nodo 78).
    // pvNodoId lo expone el backend desde cajaDispositivo.nodo_id (el nodo del DISPOSITIVO,
    // no el del usuario que cajaContext.nodoId resolvía a usuario.nodo_id).
    const urlJuego = (config && config.urlJuego) || PromoterConfig.CUSTOMER_URL_FALLBACK || '';
    const nodoId = (config && config.caja && config.caja.pvNodoId) || (user && user.nodo_id) || '';
    const baseUrl = urlJuego ? `${urlJuego.replace(/\/+$/, '')}/bienvenida` : '';

    // Link de afiliación con código OPACO que esconde { nodo, canal } — el jugador
    // que lo recibe ve un código sin sentido (no 'pv=77'), y la landing de
    // bienvenida lo decodifica para atribuir nodo + canal de procedencia. Cada
    // botón de compartir genera su link con SU canal. linkCanal('directo') es el
    // genérico. /bienvenida sin .html: CF Pages sirve clean URLs (evita redirect 3G).
    function linkCanal(canal) {
        if (!baseUrl || !nodoId) return '';
        const cod = codificarAfiliacion(nodoId, canal);
        return cod ? `${baseUrl}?r=${cod}` : `${baseUrl}?pv=${nodoId}`;
    }
    const link = linkCanal('directo'); // genérico (copy/qr/navigator.share sin canal específico)

    // Mensaje editable (persistido). Default por idioma.
    const KEY = PromoterConfig.STORAGE_KEYS.MENSAJE_COMPARTIR;
    let mensaje = localStorage.getItem(KEY);
    if (mensaje === null) mensaje = t('compartir.mensajeDefault');

    // Creatividades: mensajes pre-armados que el promotor elige de un toque (los
    // carga en el textarea). Da material listo para compartir sin pensar el copy.
    const creatividades = t('compartir.creatividades');
    const listaCreatividades = Array.isArray(creatividades) ? creatividades : [];

    // Aviso de pasarela: config.activa (pasarela-check) o caja.warnings.
    const sinPasarela = config && config.pasarela_activa === false;

    el.innerHTML = `
        <div class="pm-share-hero card-hero pm-hero-fila" style="border-color:var(--accent); box-shadow:0 8px 28px rgba(0,0,0,0.5), 0 0 36px -10px var(--accent);">
            <div class="pm-share-hero-icon">${iconRocket()}</div>
            <div class="pm-share-hero-titulo" id="pm-hero-frase">${t('compartir.heroTitulo')}</div>
        </div>

        ${sinPasarela ? `<div class="banner banner-warning" style="margin-bottom:2px;">${t('compartir.sinPasarela')}</div>` : ''}

        <div class="card">
            <!-- Compositor premium: el promotor arma su mensaje (toggle on/off + galería) -->
            <div class="pm-compositor">
                <div class="pm-comp-fila">
                    <button type="button" class="pm-comp-toggle" id="pm-toggle-mensaje" aria-pressed="true">
                        <span class="pm-comp-switch"></span>
                        <span class="pm-comp-toggle-label">${iconChat()} ${t('compartir.incluirMensaje')}</span>
                    </button>
                    <button type="button" class="pm-comp-galeria-btn" id="pm-abrir-galeria">${iconMensajes()} ${t('compartir.galeriaBtn')}</button>
                </div>
                <div id="pm-mensaje-wrap">
                    <textarea id="pm-mensaje" rows="2" class="pm-textarea"
                        inputmode="none" autocomplete="off" autocorrect="off"
                        autocapitalize="off" spellcheck="false"
                        placeholder="${t('compartir.mensajePlaceholder')}">${escapeHtml(mensaje)}</textarea>
                </div>

                <!-- Imagen (toggle): on/off + galería. Por ahora "em breve". -->
                <div class="pm-comp-fila">
                    <button type="button" class="pm-comp-toggle pm-comp-disabled" id="pm-toggle-imagen" aria-pressed="false" disabled>
                        <span class="pm-comp-switch"></span>
                        <span class="pm-comp-toggle-label">${iconImagen()} ${t('compartir.incluirImagen')}</span>
                    </button>
                    <span class="pm-comp-soon">${t('compartir.emBreve')}</span>
                </div>
            </div>

            <div class="pm-share-acciones">
                <!-- Cuadrado WhatsApp: 2 botones (novo número con pinpad · meus contatos) -->
                <div class="pm-wa-cuadro">
                    <div class="pm-wa-cuadro-head">${iconWa()} WhatsApp</div>
                    <div class="pm-wa-cuadro-botones">
                        <button class="btn pm-wa-btn pm-wa-num-trigger" id="pm-wa-num-trigger" ${link ? '' : 'disabled'}>
                            ${iconDial()}<span>${t('compartir.novoNumero')}</span>
                        </button>
                        <button class="btn pm-wa-btn btn-whatsapp" id="pm-wa-lista" ${link ? '' : 'disabled'}>
                            ${iconWa()}<span class="pm-wa-2lineas">${t('compartir.meusContatos')}</span>
                        </button>
                    </div>
                </div>
                <!-- 4 botones 2x2, cada uno su color + shine: Redes (FB·IG·TikTok) · Telegram · Copiar · QR -->
                <div class="pm-share-secundarias pm-share-grid2x2">
                    <button class="btn pm-btn-shine btn-redes" id="pm-meta" ${link ? '' : 'disabled'}>
                        <span class="pm-redes-logos">${iconFacebook()}${iconInstagram()}${iconTiktok()}</span>
                        <span>${t('compartir.redes')}</span>
                    </button>
                    <button class="btn pm-btn-shine btn-telegram" id="pm-telegram" ${link ? '' : 'disabled'}>${iconTelegram()}<span>Telegram</span></button>
                    <button class="btn pm-btn-shine btn-copiar" id="pm-copy" ${link ? '' : 'disabled'}>${iconCopy()}<span>${t('compartir.copiar')}</span></button>
                    <button class="btn pm-btn-shine btn-qr" id="pm-qr" ${link ? '' : 'disabled'}>${iconQr()}<span>${t('compartir.qr')}</span></button>
                </div>
            </div>
        </div>`;

    // Ícono del hero: cohete SVG (temático de compartir/despegar), animado por CSS
    // (flota + la llama parpadea). El .json de prueba era un trofeo repetido (igual
    // que Ranking) → mejor el cohete propio. Cuando el dueño baje un Lottie de
    // compartir/cohete premium a /assets/lottie/compartir.json, descomentar el
    // montarLottie de abajo y aparece animado solo.
    const heroIcon = el.querySelector('.pm-share-hero-icon');
    if (heroIcon) heroIcon.innerHTML = iconRocket();

    // Frases rotativas en el hero (el mensaje fijo aburre — rotan con fade).
    const fraseEl = el.querySelector('#pm-hero-frase');
    if (fraseEl) rotarFrases(fraseEl);

    const mensajeEl = el.querySelector('#pm-mensaje');

    // Mini-editor de formato (barra B/I/cita + teclado custom + PREVIEW MULTICANAL)
    // — el promotor compone con formato y ve cómo se verá en WhatsApp/Telegram/Redes.
    const barraEditor = montarEditorMensaje(
        mensajeEl,
        () => {
            localStorage.setItem(KEY, mensajeEl.value);
        },
        () => link // el preview muestra el link (canal directo) junto al mensaje
    );
    mensajeEl.insertAdjacentElement('beforebegin', barraEditor);

    // Persistir el mensaje al editarlo.
    mensajeEl.addEventListener('input', () => {
        localStorage.setItem(KEY, mensajeEl.value);
    });

    // Toggle "incluir mensaje" on/off — si off, solo se comparte el link.
    const toggleMensaje = el.querySelector('#pm-toggle-mensaje');
    const mensajeWrap = el.querySelector('#pm-mensaje-wrap');
    let mensajeActivo = localStorage.getItem(KEY + '_off') !== '1';
    function pintarToggleMensaje() {
        toggleMensaje.setAttribute('aria-pressed', String(mensajeActivo));
        mensajeWrap.style.display = mensajeActivo ? '' : 'none';
    }
    pintarToggleMensaje();
    toggleMensaje.addEventListener('click', () => {
        mensajeActivo = !mensajeActivo;
        localStorage.setItem(KEY + '_off', mensajeActivo ? '0' : '1');
        pintarToggleMensaje();
    });

    // Galería de mensajes: presets pre-armados + los GUARDADOS por el promotor.
    // Abre un modal donde elige uno, o guarda el actual como suyo. (Lo emocionante:
    // que arme y guarde sus propias creatividades.)
    el.querySelector('#pm-abrir-galeria').addEventListener('click', () => {
        abrirGaleriaMensajes(listaCreatividades, mensajeEl.value, (texto) => {
            mensajeEl.value = texto;
            localStorage.setItem(KEY, texto);
            if (!mensajeActivo) {
                mensajeActivo = true;
                localStorage.setItem(KEY + '_off', '0');
                pintarToggleMensaje();
            }
        });
    });

    // Texto a compartir POR CANAL: usa el link con el código de ESE canal (tracking)
    // + el mensaje. CLAVE: para 'redes' (Instagram/Facebook, que NO renderizan
    // formato) se manda TEXTO PLANO — sin los *_~> que se verían como basura. Para
    // WhatsApp/Telegram se mandan los símbolos (esas apps los renderizan).
    function textoCompartir(canal) {
        const l = linkCanal(canal);
        let msg = mensajeActivo ? (mensajeEl.value || '').trim() : '';
        if (msg && canal === 'redes') msg = aTextoPlano(msg);
        return msg ? `${msg} ${l}` : l;
    }

    // WhatsApp "a mis contactos / grupos": wa.me SIN número → WhatsApp abre su
    // propio selector, donde el promotor elige un chat o un GRUPO de su lista.
    el.querySelector('#pm-wa-lista').addEventListener('click', () => {
        if (!link) return;
        window.open(`https://wa.me/?text=${encodeURIComponent(textoCompartir('wa_lista'))}`, '_blank', 'noopener');
    });

    // WhatsApp "novo número": abre el pinpad (teclado propio) para teclear el
    // número con código de país; al confirmar, wa.me/<número> abre el chat directo.
    el.querySelector('#pm-wa-num-trigger').addEventListener('click', () => {
        if (!link) return;
        abrirPinpadNumero((num) => {
            window.open(`https://wa.me/${num}?text=${encodeURIComponent(textoCompartir('wa_num'))}`, '_blank', 'noopener');
        });
    });

    // Telegram: share nativo (abre Telegram con el mensaje + link para elegir chat).
    el.querySelector('#pm-telegram').addEventListener('click', () => {
        if (!link) return;
        const l = linkCanal('telegram');
        const url = `https://t.me/share/url?url=${encodeURIComponent(l)}&text=${encodeURIComponent((mensajeEl.value || '').trim())}`;
        window.open(url, '_blank', 'noopener');
    });

    el.querySelector('#pm-meta').addEventListener('click', () => {
        if (!link) return;
        const l = linkCanal('redes');
        // Facebook sharer (IG no tiene deeplink de share — el usuario pega el
        // link copiado en su story/bio). Si el navegador soporta Web Share API
        // nativa, la usamos primero (abre el selector con FB/IG/etc).
        if (navigator.share) {
            navigator.share({ text: textoCompartir('redes'), url: l }).catch(() => {});
            return;
        }
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(l)}`, '_blank', 'noopener');
    });

    el.querySelector('#pm-copy').addEventListener('click', async (e) => {
        if (!link) return;
        // Capturar el botón ANTES del await: tras un await, e.currentTarget es null.
        const btn = e.currentTarget;
        try {
            await navigator.clipboard.writeText(textoCompartir('copy'));
        } catch (_) {
            // Fallback navegadores viejos: prompt para copiar manual.
            window.prompt(t('compartir.copiar'), textoCompartir('copy'));
        }
        if (!btn) return;
        const original = btn.innerHTML;
        btn.innerHTML = `✓ ${t('compartir.copiado')}`;
        setTimeout(() => {
            btn.innerHTML = original;
        }, 1500);
    });

    el.querySelector('#pm-qr').addEventListener('click', () => {
        if (!link) return;
        mostrarQR(linkCanal('qr'));
    });

    return el;
}

// Galería de mensajes — modal creativo. Muestra los presets pre-armados + los que
// el promotor GUARDÓ (localStorage). Puede elegir uno o guardar el actual como suyo.
// Que se sienta creativo y conserve sus cosas.
const KEY_PRESETS_GUARDADOS = 'pm_mensajes_guardados';
function leerPresetsGuardados() {
    try {
        const raw = localStorage.getItem(KEY_PRESETS_GUARDADOS);
        const arr = raw ? JSON.parse(raw) : [];
        return Array.isArray(arr) ? arr : [];
    } catch (_) {
        return [];
    }
}
function guardarPresets(arr) {
    try {
        localStorage.setItem(KEY_PRESETS_GUARDADOS, JSON.stringify(arr.slice(0, 30)));
    } catch (_) {}
}

function abrirGaleriaMensajes(prearmados, mensajeActual, onElegir) {
    const overlay = document.createElement('div');
    overlay.className = 'pm-galeria-overlay';
    let tabActivo = 'sugeridos'; // arranca en sugeridos (siempre hay)

    function render() {
        const guardados = leerPresetsGuardados();
        // Si no hay guardados, forzar tab sugeridos.
        if (!guardados.length) tabActivo = 'sugeridos';
        const listaMios = guardados
            .map(
                (m, i) =>
                    `<div class="pm-galeria-item pm-galeria-mio" data-texto="${escapeHtml(m)}">
                        <span class="pm-galeria-item-txt">${formatoAHtml(m)}</span>
                        <button class="pm-galeria-del" data-del="${i}" aria-label="borrar">🗑</button>
                    </div>`
            )
            .join('');
        const listaSug = (prearmados || [])
            .map(
                m =>
                    `<div class="pm-galeria-item" data-texto="${escapeHtml(m)}">
                        <span class="pm-galeria-item-txt">${formatoAHtml(m)}</span>
                    </div>`
            )
            .join('');
        const vacioMios = `<div class="pm-galeria-vacio">${escapeHtml(t('compartir.misMensajesVacio'))}</div>`;

        overlay.innerHTML = `
            <div class="pm-galeria-modal" role="dialog" aria-modal="true">
                <div class="pm-galeria-head">
                    <div class="pm-galeria-titulo">${iconMensajes()} ${escapeHtml(t('compartir.galeria'))}</div>
                    <button class="pm-galeria-x" id="pm-gal-x" aria-label="cerrar">✕</button>
                </div>

                <button class="pm-galeria-guardar" id="pm-gal-guardar" ${mensajeActual && mensajeActual.trim() ? '' : 'disabled'}>
                    ${iconGaleria()} ${escapeHtml(t('compartir.guardarActual'))}
                </button>

                <div class="pm-galeria-tabs">
                    <button type="button" class="pm-galeria-tab ${tabActivo === 'sugeridos' ? 'active' : ''}" data-tab="sugeridos">${escapeHtml(t('compartir.sugeridos'))}</button>
                    <button type="button" class="pm-galeria-tab ${tabActivo === 'mios' ? 'active' : ''}" data-tab="mios">${escapeHtml(t('compartir.misMensajes'))}${guardados.length ? ` <span class="pm-galeria-badge">${guardados.length}</span>` : ''}</button>
                </div>

                <div class="pm-galeria-lista" id="pm-galeria-scroll">
                    ${tabActivo === 'mios' ? listaMios || vacioMios : listaSug}
                </div>
            </div>`;
    }

    function cerrar() {
        overlay.classList.remove('open');
        setTimeout(() => overlay.remove(), 200);
    }

    overlay.addEventListener('click', (e) => {
        if (e.target === overlay || e.target.id === 'pm-gal-x') {
            cerrar();
            return;
        }
        // Cambiar de tab (Sugeridos / Mis mensajes) — cada uno scrollea independiente.
        const tab = e.target.closest('[data-tab]');
        if (tab) {
            tabActivo = tab.dataset.tab;
            render();
            requestAnimationFrame(() => overlay.classList.add('open'));
            return;
        }
        // Borrar un mensaje guardado.
        const del = e.target.closest('[data-del]');
        if (del) {
            e.stopPropagation();
            const guardados = leerPresetsGuardados();
            guardados.splice(parseInt(del.dataset.del, 10), 1);
            guardarPresets(guardados);
            render();
            requestAnimationFrame(() => overlay.classList.add('open'));
            return;
        }
        // Guardar el mensaje actual como preset propio → saltar al tab "mios".
        if (e.target.closest('#pm-gal-guardar')) {
            const txt = (mensajeActual || '').trim();
            if (txt) {
                const guardados = leerPresetsGuardados();
                if (!guardados.includes(txt)) {
                    guardados.unshift(txt);
                    guardarPresets(guardados);
                    tabActivo = 'mios';
                    render();
                    requestAnimationFrame(() => overlay.classList.add('open'));
                }
            }
            return;
        }
        // Elegir un mensaje.
        const item = e.target.closest('.pm-galeria-item');
        if (item) {
            onElegir(item.dataset.texto);
            cerrar();
        }
    });

    render();
    document.body.appendChild(overlay);
    requestAnimationFrame(() => overlay.classList.add('open'));
}

// Pinpad propio del promoter para teclear un número de teléfono (largo variable,
// con código de país). Premium, coherente con el panel. Al confirmar llama onOk
// con el número (solo dígitos). Inspirado en el PinPad del front-customer pero
// simple (número libre, no PIN de 4).
function abrirPinpadNumero(onOk) {
    let num = '';
    const overlay = document.createElement('div');
    overlay.className = 'pm-pin-overlay';
    const teclas = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '+', '0', '⌫'];
    overlay.innerHTML = `
        <div class="pm-pin-modal" role="dialog" aria-modal="true">
            <div class="pm-pin-titulo">${escapeHtml(t('compartir.novoNumero'))}</div>
            <div class="pm-pin-hint">${escapeHtml(t('compartir.waNumPlaceholder'))}</div>
            <div class="pm-pin-display" id="pm-pin-display">+</div>
            <div class="pm-pin-keys">
                ${teclas.map((k) => `<button class="pm-pin-key" data-k="${k}">${k}</button>`).join('')}
            </div>
            <div class="pm-pin-acciones">
                <button class="btn btn-copy" id="pm-pin-cancel">${t('compartir.cerrar')}</button>
                <button class="btn btn-whatsapp" id="pm-pin-ok">${iconWa()} WhatsApp</button>
            </div>
        </div>`;

    const display = overlay.querySelector('#pm-pin-display');
    function pintar() {
        display.textContent = num ? '+' + num : '+';
    }
    function cerrar() {
        overlay.classList.remove('open');
        setTimeout(() => overlay.remove(), 200);
    }

    overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
            cerrar();
            return;
        }
        const key = e.target.closest('.pm-pin-key');
        if (key) {
            const k = key.dataset.k;
            if (k === '⌫') num = num.slice(0, -1);
            else if (k === '+') {
                /* el + ya está implícito al inicio; ignorar */
            } else if (num.length < 15) num += k;
            pintar();
            if (navigator.vibrate) navigator.vibrate(20);
            return;
        }
        if (e.target.closest('#pm-pin-cancel')) cerrar();
        if (e.target.closest('#pm-pin-ok')) {
            if (num.length < 6) {
                display.classList.add('pm-pin-error');
                setTimeout(() => display.classList.remove('pm-pin-error'), 600);
                return;
            }
            cerrar();
            onOk(num);
        }
    });

    document.body.appendChild(overlay);
    requestAnimationFrame(() => overlay.classList.add('open'));
    pintar();
}

// Modal con el QR del link de afiliación — premium, dark, integrado. Reusa
// window.QRCode (qrcode-lib de shared). El promotor lo muestra/imprime para
// captar offline. El QR va sobre una "tarjeta" blanca redondeada (necesario para
// que escanee bien) pero el modal y el resto son dark, coherentes con el panel.
function mostrarQR(link) {
    const overlay = document.createElement('div');
    overlay.className = 'pm-qr-overlay';
    overlay.innerHTML = `
        <div class="pm-qr-modal" role="dialog" aria-modal="true">
            <div class="pm-qr-titulo">${escapeHtml(t('compartir.qr'))}</div>
            <div class="pm-qr-marco">
                <div id="pm-qr-canvas"></div>
            </div>
            <div class="pm-qr-sub">${escapeHtml(t('compartir.qrSub'))}</div>
            <button class="btn btn-copy pm-qr-cerrar" id="pm-qr-cerrar">${t('compartir.cerrar')}</button>
        </div>`;

    function cerrar() {
        overlay.classList.remove('open');
        setTimeout(() => overlay.remove(), 200);
    }
    overlay.addEventListener('click', (e) => {
        if (e.target === overlay || e.target.id === 'pm-qr-cerrar') cerrar();
    });
    document.body.appendChild(overlay);
    requestAnimationFrame(() => overlay.classList.add('open'));

    const cont = overlay.querySelector('#pm-qr-canvas');
    if (typeof window.QRCode !== 'undefined') {
        new window.QRCode(cont, {
            text: link,
            width: 300,
            height: 300,
            colorDark: '#0a1628',
            colorLight: '#ffffff',
            correctLevel: window.QRCode.CorrectLevel.M,
        });
    } else {
        cont.textContent = link;
    }
}

// ── Íconos inline ──────────────────────────────────────────────────────
// Cohete FILLED potente (no stroke fino) — gradiente azul + llama. Animado por
// CSS (.pm-share-hero-icon flota + la llama parpadea). Íconos propios, sin licencia.
function iconRocket() {
    return `<svg width="56" height="56" viewBox="0 0 24 24" fill="none">
        <defs>
            <linearGradient id="pmRocketG" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stop-color="#7ab3ff"/><stop offset="1" stop-color="#3b82f6"/>
            </linearGradient>
        </defs>
        <path class="pm-rocket-flame" d="M9.5 16.5c-.6.9-1 2.3-1.1 3.6 1.3-.1 2.7-.5 3.6-1.1.7-.5.7-1.4.1-2-.6-.6-2-.5-2.6-.5z" fill="#f5c451"/>
        <path d="M14.5 3.2c3.3-1.1 6.3-.8 6.3-.8s.3 3-.8 6.3c-.8 2.4-2.4 4.3-4 5.6l-1.3 1.3a1 1 0 0 1-.7.3H9.8a1 1 0 0 1-.7-.3l-1.5-1.5a1 1 0 0 1-.3-.7V9.8a1 1 0 0 1 .3-.7L8.9 7.8c1.3-1.6 3.2-3.8 5.6-4.6z" fill="url(#pmRocketG)"/>
        <circle cx="14.5" cy="9.5" r="1.7" fill="#0e1726"/>
        <path d="M6.5 14c-1 .3-1.8 1-2.3 1.8M9 17.5c.3 1-.1 2-1 2.8" stroke="#7ab3ff" stroke-width="1.4" stroke-linecap="round"/>
    </svg>`;
}
function iconWa() {
    return '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M17.5 14.4c-.3-.1-1.7-.8-1.9-.9-.3-.1-.5-.1-.7.1-.2.3-.7.9-.9 1.1-.2.2-.3.2-.6.1-.3-.1-1.2-.5-2.3-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.5.1-.6.1-.1.3-.3.4-.5.1-.2.2-.3.3-.5 0-.2 0-.4-.1-.5-.1-.1-.7-1.6-.9-2.2-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.4s1.1 2.8 1.2 3c.1.2 2.1 3.2 5 4.5.7.3 1.2.5 1.7.6.7.2 1.3.2 1.8.1.6-.1 1.7-.7 1.9-1.3.2-.7.2-1.2.2-1.3-.1-.2-.3-.2-.6-.3zM12 2a10 10 0 0 0-8.5 15.3L2 22l4.8-1.5A10 10 0 1 0 12 2z"/></svg>';
}
function iconDial() {
    return '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/></svg>';
}
function iconTelegram() {
    return '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M21.94 4.39 18.6 20.1c-.25 1.1-.9 1.38-1.83.86l-5.06-3.73-2.44 2.35c-.27.27-.5.5-1.02.5l.36-5.16L17.4 6.5c.41-.36-.09-.56-.63-.2L6.4 13.04l-4.94-1.55c-1.07-.34-1.1-1.07.22-1.59L20.55 2.5c.9-.33 1.68.2 1.39 1.89z"/></svg>';
}
// Logos de redes (cada uno con su color de marca via fill).
function iconFacebook() {
    return '<svg width="20" height="20" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12a12 12 0 1 0-13.9 11.9v-8.4H7.1V12h3V9.4c0-3 1.8-4.6 4.5-4.6 1.3 0 2.6.2 2.6.2v2.9h-1.5c-1.4 0-1.9.9-1.9 1.8V12h3.3l-.5 3.5h-2.8v8.4A12 12 0 0 0 24 12z"/></svg>';
}
function iconInstagram() {
    return '<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><defs><radialGradient id="pmIg" cx="0.3" cy="1" r="1"><stop offset="0" stop-color="#fdf497"/><stop offset="0.3" stop-color="#fd5949"/><stop offset="0.6" stop-color="#d6249f"/><stop offset="1" stop-color="#285AEB"/></radialGradient></defs><rect x="2" y="2" width="20" height="20" rx="6" fill="url(#pmIg)"/><circle cx="12" cy="12" r="4.2" fill="none" stroke="#fff" stroke-width="2"/><circle cx="17" cy="7" r="1.3" fill="#fff"/></svg>';
}
function iconTiktok() {
    return '<svg width="20" height="20" viewBox="0 0 24 24" fill="#fff"><path d="M16.5 5.8c-1-.7-1.6-1.7-1.8-2.8h-3v12.2c0 1.4-1.1 2.5-2.5 2.5S6.7 16.6 6.7 15.2s1.1-2.5 2.5-2.5c.3 0 .5 0 .8.1v-3a5.5 5.5 0 1 0 4.8 5.4V9.3a7 7 0 0 0 3.9 1.2V7.5c-.9 0-1.6-.3-2.2-.8z"/></svg>';
}
function iconChat() {
    return '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>';
}
function iconImagen() {
    return '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21"/></svg>';
}
function iconGaleria() {
    return '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>';
}
function iconMensajes() {
    return '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 9a2 2 0 0 1-2 2H6l-4 4V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2z"/><path d="M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1"/></svg>';
}
function iconCopy() {
    return '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="8" y="8" rx="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>';
}
function iconQr() {
    return '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="5" height="5" x="3" y="3" rx="1"/><rect width="5" height="5" x="16" y="3" rx="1"/><rect width="5" height="5" x="3" y="16" rx="1"/><path d="M21 16h-3a2 2 0 0 0-2 2v3"/><path d="M21 21v.01"/><path d="M12 7v3a2 2 0 0 1-2 2H7"/><path d="M3 12h.01"/><path d="M12 3h.01"/><path d="M12 16v.01"/><path d="M16 12h1"/><path d="M21 12v.01"/><path d="M12 21v-1"/></svg>';
}

